import React, { useMemo } from 'react';
import { Descriptions } from 'antd';
import {
  ElectricityDescriptionsDetails,
  getElectricityDetailDescriptions,
} from 'components/descriptions/meter_details/electricity';
import {
  getThermalDetailDescriptions,
  ThermalDescriptionsDetails,
} from 'components/descriptions/meter_details/thermal';
import { getWaterDetailDescriptions, WaterDescriptionsDetails } from 'components/descriptions/meter_details/water';
import { getGasDetailDescriptions } from 'components/descriptions/meter_details/gas';
import { getOtherDetailDescriptions } from 'components/descriptions/meter_details/other';
import { accessibilityTranslated } from 'components/Form/meter_details/constants';
import { FormattedMessage, IntlShape, useIntl } from 'react-intl';
import { MeterDetailGas, MeterDetailOther, SerializedMeter } from '@enmon/commons/lib/enmon';
import { Commodity, Nullable } from '@enmon/commons';
import { useStore } from '../../../useStore';
import { ImageUploadReview } from '../../../../../components/ImageUpload/ImageUploadReview';
import { negatePositiveNumber } from '../../../../../utils/math';
import { DetailDescriptions as messages } from '../messages';
import { StepMeterDetails } from '../../../types';

function getAdditionalFields(
  intl: IntlShape,
  commodity: Nullable<SerializedMeter['commodity']>,
  details: StepMeterDetails | Record<string, never> = {},
) {
  switch (commodity) {
    case Commodity.Thermal: {
      return getThermalDetailDescriptions(details as ThermalDescriptionsDetails, intl);
    }
    case Commodity.Water: {
      return getWaterDetailDescriptions(details as WaterDescriptionsDetails, intl);
    }
    case Commodity.Electricity: {
      return getElectricityDetailDescriptions(details as ElectricityDescriptionsDetails, intl);
    }
    case Commodity.NaturalGas: {
      return getGasDetailDescriptions(details as MeterDetailGas, intl);
    }
    case Commodity.Other: {
      return getOtherDetailDescriptions(details as MeterDetailOther, intl);
    }
    default:
      console.error(`Third step with unknown commodity: ${commodity ?? 'undefined'}`);
      return null;
  }
}

export const DetailDescriptions: React.VFC = () => {
  const intl = useIntl();
  const { lorawanRssi, nbiotRssi, accessibility, lat, lng, commodity, photoDetailList, serialNumber, detail } =
    useStore();

  const additionalFields = useMemo(() => getAdditionalFields(intl, commodity, detail), [detail, intl, commodity]);

  return (
    <Descriptions
      bordered
      column={{ xxl: 2, xl: 2, lg: 2, md: 2, sm: 1, xs: 1 }}
      size="small"
      title={<FormattedMessage {...messages.title} />}
    >
      <Descriptions.Item label={<FormattedMessage {...messages.detailPhotosLabel} />} span={2}>
        <ImageUploadReview fileList={photoDetailList} previewWidth={150} />
      </Descriptions.Item>
      <Descriptions.Item label={<FormattedMessage {...messages.serialNumber} />}>{serialNumber}</Descriptions.Item>
      <Descriptions.Item label={<FormattedMessage {...messages.accessibility} />} span={2}>
        {accessibility?.map(access => intl.formatMessage(accessibilityTranslated[access])).join(', ')}
      </Descriptions.Item>
      <Descriptions.Item label={<FormattedMessage {...messages.lorawanRssi} />}>
        {lorawanRssi
          ? `${negatePositiveNumber(lorawanRssi) ?? 'INVALID NUMBER'} dB`
          : intl.formatMessage(messages.unmeasured)}
      </Descriptions.Item>
      <Descriptions.Item label={<FormattedMessage {...messages.nbiotRssi} />}>
        {nbiotRssi
          ? `${negatePositiveNumber(nbiotRssi) ?? 'INVALID NUMBER'} dB`
          : intl.formatMessage(messages.unmeasured)}
      </Descriptions.Item>
      <Descriptions.Item label="Lat">{lat}</Descriptions.Item>
      <Descriptions.Item label="Lng">{lng}</Descriptions.Item>

      {additionalFields}
    </Descriptions>
  );
};
